import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCircleCheck,
  faTriangleExclamation,
  faMagnifyingGlass,
  faXmark,
  faUserGraduate,
  faChalkboardUser,
} from '@fortawesome/free-solid-svg-icons';
import './Pending.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const LATE_DAYS = 2;

const ageLabel = (date) => {
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const Photo = ({ src }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="rmapf-avatar">
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : <FontAwesomeIcon icon={faUserGraduate} />}
    </span>
  );
};

// Read-only list for the admin: which classes still wait for the TEACHER's feedback
const Pending = ({ userData }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teacher, setTeacher] = useState('all');
  const [query, setQuery] = useState('');

  // ---------- Load (same endpoint) ----------
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/adminfeedback`, auth());
      const list = (res.data?.pendingFeedbacks || [])
        .map((f) => ({
          ...f,
          date: new Date(f.ClassDateTime),
          teacherName: [f.TFirstName, f.TLastName].filter(Boolean).join(' ') || 'Unknown teacher',
          studentName: [f.FirstName, f.LastName].filter(Boolean).join(' ') || 'Student',
        }))
        .sort((a, b) => a.date - b.date); // oldest (most overdue) first
      setFeedbacks(list);
      setError('');
    } catch (err) {
      console.error('Error fetching pending feedbacks:', err);
      setError('We couldn’t load the pending feedback.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userData?.id) return;
    setLoading(true);
    load();
  }, [userData?.id, load]);

  // teachers with how many feedbacks each one still owes
  const teachers = useMemo(() => {
    const map = {};
    feedbacks.forEach((f) => {
      map[f.teacherName] = (map[f.teacherName] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [feedbacks]);

  const lateCount = feedbacks.filter((f) => Date.now() - f.date.getTime() > LATE_DAYS * 86400000).length;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return feedbacks.filter(
      (f) => (teacher === 'all' || f.teacherName === teacher) && (!q || f.studentName.toLowerCase().includes(q) || f.teacherName.toLowerCase().includes(q))
    );
  }, [feedbacks, teacher, query]);

  if (loading) {
    return (
      <div className="rmapf" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rmapf-skel" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmapf">
        <div className="rmapf-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmapf-btn" onClick={() => { setLoading(true); load(); }}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <div className="rmapf">
        <div className="rmapf-empty">
          <FontAwesomeIcon icon={faCircleCheck} className="is-done" />
          <p>All caught up. Every teacher has sent their feedback.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rmapf">
      <div className="rmapf-head">
        <p className="rmapf-intro">
          <strong>{feedbacks.length}</strong> {feedbacks.length === 1 ? 'class is' : 'classes are'} waiting for the teacher’s feedback
          {lateCount > 0 && (
            <span className="rmapf-late-note">
              {' '}· <b>{lateCount}</b> older than {LATE_DAYS} days
            </span>
          )}
        </p>
        <label className="rmapf-search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <span className="rmapf-sr">Search student or teacher</span>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Student or teacher" />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </label>
      </div>

      {/* per-teacher filter */}
      <div className="rmapf-teachers" role="radiogroup" aria-label="Teacher">
        <button type="button" role="radio" aria-checked={teacher === 'all'} className={teacher === 'all' ? 'is-on' : ''} onClick={() => setTeacher('all')}>
          All <span>{feedbacks.length}</span>
        </button>
        {teachers.map(([name, n]) => (
          <button key={name} type="button" role="radio" aria-checked={teacher === name} className={teacher === name ? 'is-on' : ''} onClick={() => setTeacher(name)}>
            <FontAwesomeIcon icon={faChalkboardUser} /> {name} <span>{n}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="rmapf-empty">
          <p>No class matches your search.</p>
        </div>
      ) : (
        <ul className="rmapf-list">
          {shown.map((f, i) => {
            const isPaid = f.Type === 0;
            const valid = !Number.isNaN(f.date.getTime());
            const late = valid && Date.now() - f.date.getTime() > LATE_DAYS * 86400000;
            return (
              <li key={f.Id ?? i} className={`rmapf-card is-${isPaid ? 'paid' : 'demo'}`}>
                <div className="rmapf-main">
                  <Photo src={f.Image} />
                  <div className="rmapf-who">
                    <h3>
                      {f.studentName}
                      {f.IdUser && (
                        <a href={`/Admin/studentprofile/${f.IdUser}`} target="_blank" rel="noopener noreferrer" aria-label="Open student profile">
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        </a>
                      )}
                    </h3>
                    <p>
                      <FontAwesomeIcon icon={faChalkboardUser} /> {f.teacherName}
                    </p>
                    <div className="rmapf-tags">
                      <span className={`rmapf-tag is-${isPaid ? 'paid' : 'demo'}`}>{isPaid ? 'Paid' : 'Demo'}</span>
                      {valid && <span className={`rmapf-tag ${late ? 'is-late' : ''}`}>{ageLabel(f.date)}</span>}
                    </div>
                  </div>
                </div>

                {valid && (
                  <div className="rmapf-when">
                    <span>{f.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    <strong>{f.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}</strong>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Pending;